import React, { useLayoutEffect, useRef } from "react";
import "./EnvironmentBanner.scss";

// U1b — hard-to-miss non-production environment banner (owner requirement
// 2026-07-02: "make the staging banner difficult to miss so operators can
// never confuse environments"). Renders on every authenticated page, sticky
// under the fixed navbar. Production renders nothing.

const ENV = process.env.REACT_APP_ENV || "development";

const LABELS: Record<string, string> = {
  staging: "STAGING ENVIRONMENT",
  development: "DEVELOPMENT (localhost → staging APIs)",
};

const EnvironmentBanner: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);

  // Publish the banner's real rendered height for the global modal layout
  // standard (styles/modal-standard.scss). The banner can grow when its text
  // wraps at narrow viewports or high zoom, so a hardcoded constant would
  // leave modals clipped; production renders no banner and keeps the 0px
  // default.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const publish = () => {
      document.documentElement.style.setProperty(
        "--kk-banner-height",
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      );
    };
    publish();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(publish) : null;
    observer?.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", publish);
      document.documentElement.style.setProperty("--kk-banner-height", "0px");
    };
  }, []);

  if (ENV === "production") return null;
  const label = LABELS[ENV] || `${ENV.toUpperCase()} ENVIRONMENT`;
  return (
    <div
      ref={ref}
      className="kk-environment-banner"
      role="status"
      aria-label={label}
    >
      <span className="kk-environment-banner-icon" aria-hidden="true">
        ⚠
      </span>
      <span className="kk-environment-banner-text">
        {label} — test data only. This is <strong>not production</strong>.
      </span>
      <span className="kk-environment-banner-icon" aria-hidden="true">
        ⚠
      </span>
    </div>
  );
};

export default EnvironmentBanner;
